import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_SAVED_PROPERTIES } from '../../../lib/graphql/queries/contacts';
import type { GQLSavedPropertyItem } from './useContact';

const DEFAULT_PAGE_SIZE = 25;

interface GetContactSavedPropertiesData {
  contact: {
    contactId: string;
    savedProperties: {
      items: GQLSavedPropertyItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactSavedPropertiesGQL = (contactId: string, pageSize = DEFAULT_PAGE_SIZE) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useQuery<GetContactSavedPropertiesData>(
    GET_CONTACT_SAVED_PROPERTIES,
    {
      variables: {
        organizationId: organization?.id ?? '',
        contactId,
        limit: pageSize,
        offset: currentPage * pageSize,
        sortOrder: 'DESC',
      },
      skip: !organization?.id || !contactId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const items = data?.contact?.savedProperties?.items ?? [];
  const total = data?.contact?.savedProperties?.total ?? 0;
  const hasMore = data?.contact?.savedProperties?.hasMore ?? false;

  const handleNextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return { items, total, hasMore, loading, error, hasNext: hasMore, handleNextPage, resetPage };
};
